import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { change, Field } from 'redux-form';
import { t } from 'i18next';
import Column from '../../ReactComponent/Column';
import Row from '../../ReactComponent/Row';
import RenderField from '../../ReactComponent/RenderField';
import { isRequired, isInRange } from '../../../utils/validator';
import { currencyOnly } from '../../../utils/mask';
import { FormSummary, PageTitle, Icon } from '../css';
import { P } from '../../../theme/typography';
import PrimaryButton from '../../ReactComponent/PrimaryButton';
import ArrowLeftOrange from '../../../assets/icons/ArrowLeftOrange.svg';
import SecondaryButton from '../../ReactComponent/SecondaryButton';

import { Divisions } from '../../../utils/enums';

const oneTo4999 = isInRange(1, 4999.99);

const Step3 = ({
  formData = {}, step, accessorialsList = [], parentFields, dispatch, formName, prevStep, nextStep,
}) => {
  const { measurementList, division } = formData;
  const [dangerousGoodFound, setDangerousGoodFound] = useState(false);
  const dangerCode = division === Divisions.Sameday.name ? 'HAZARD' : 'DANGER';

  useEffect(() => {
    if (!measurementList) {
      return;
    }

    // Determine if any of the measurement items have been flagged as having dangerous goods...
    setDangerousGoodFound(measurementList.some((element) => (element.itemDangerous === true)));
  }, [measurementList]);

  useEffect(() => {
    // Construct ACCESSORIAL value as being unselected...
    const newDangerAccessorialValue = ['']; // Must be array w/ empty element, because of dependency on indeces. See ../api.js > accessorialsList()...

    // If the "DANGER" accessorial is included in the accessorialList and an item has dangerous goods,
    // ensure the "Dangerous Goods" control is selected in the UI...
    const dangerAccessorial = accessorialsList.find((element) => element.code === dangerCode);
    if (dangerAccessorial && dangerousGoodFound) {
      // Amend ACCESSORIAL value as being selected...
      newDangerAccessorialValue.push({
        name: dangerAccessorial.description,
        code: dangerAccessorial.code,
        accessorialId: dangerAccessorial.accessorialId,
      });
    }

    // Set the DANGER accessorials value, in the store...
    dispatch(change(formName, `accessorials.${dangerCode}.selected`, newDangerAccessorialValue));
  }, [dangerousGoodFound, accessorialsList]);

  if (step === 2) {
    const { accessorials } = formData;

    return (
      <FormSummary>
        <PrimaryButton iconRightArrowWhite smallerWidth RightButtonMargin type="button" onClick={nextStep} className="float-right">
          {t('Next')}
        </PrimaryButton>

        <SecondaryButton type="button" smallerWidth onClick={prevStep} className="float-right">
          <Icon className="icon icon-left topLeftIcon" src={ArrowLeftOrange} alt="back" />
          {t('Back')}
        </SecondaryButton>

        <PageTitle>{parentFields.AdditionalServicesPageTitle && parentFields.AdditionalServicesPageTitle.value}</PageTitle>
        <P>{t('AdditionalChargesMayApply')}</P>
        <Row>
          {accessorialsList.map((service, index) => (
            <Column $tablet={6} $mobile={12} key={index}>
              <Field
                name={`accessorials.${service.code}.selected`}
                type="checkboxes"
                data={[{ name: service.description, code: service.code, accessorialId: service.accessorialId }]}
                component={RenderField}
                multiple
                value={service.code}
                disabled={service.code === dangerCode}
              />
              <P>{t(`accessorialDesc${service.code}`)}</P>
              {accessorials[service.code] && accessorials[service.code].selected.length > 1 && service.isValueRequired && (
              <Row style={{ marginBottom: '40px' }}>
                <Column $tablet={8} $mobile={12}>
                  <Field
                    name={`accessorials.${service.code}.value`}
                    type="single"
                    label={t('amount')}
                    required
                    component={RenderField}
                    validate={[isRequired, oneTo4999]}
                    normalize={currencyOnly}
                  />
                </Column>
                <Column $tablet={4} $mobile={12}>
                  <Field
                    name={`accessorials.${service.code}.currency`}
                    type="dropdown"
                    label={t('currency')}
                    data={['CAD', 'USD']}
                    required
                    component={RenderField}
                    validate={[isRequired]}
                  />
                </Column>
              </Row>
              )}
            </Column>
          ),
          )}
          <Column $tablet={12} $mobile={12}>
            <P style={{ marginTop: '60px' }}>
              <strong>
                {parentFields.AdditionalServicesSummary
                        && parentFields.AdditionalServicesSummary.value}
              </strong>
            </P>
          </Column>
        </Row>
      </FormSummary>
    );
  }

  return null;
};

Step3.propTypes = {
  formData: PropTypes.shape({
    measurementList: PropTypes.array,
    division: PropTypes.string,
    accessorials: PropTypes.object,
  }),
  step: PropTypes.number,
  accessorialsList: PropTypes.array,
  parentFields: PropTypes.shape({
    AdditionalServicesPageTitle: PropTypes.object,
    AdditionalServicesSummary: PropTypes.object,
  }),
  dispatch: PropTypes.func,
  formName: PropTypes.string,
  prevStep: PropTypes.func,
  nextStep: PropTypes.func,
};

export default Step3;
